import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';

import {
  ClientTotalsRecapArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs
} from '../../../../models/reports';
import { CheckboxSizes } from '../../../../components/checkbox/checkbox.component';
import { CompaniesService } from '../../../../services/companies.service';
import { GetQuarter, GetQuarterEnd, GetQuarterStart } from '../../../../utils/quarter-utils';
import { PickerValue } from '../../../../components/generic-picker/generic-picker.component';

@Component({
  selector: 'lib-client-totals-recap',
  templateUrl: './client-totals-recap.component.html',
  styleUrls: ['./client-totals-recap.component.css'],
  standalone: false
})
export class ClientTotalsRecapComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public types = ClientTotalsRecapTypes;
  public checkSizes = CheckboxSizes;

  public form: UntypedFormGroup;
  public get reportType() {
    return this.form.get('reportType').value;
  }

  private readonly ENDPOINT = 'ClientTotalsRecap';
  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService
  ) {}

  ngOnInit(): void {
    this.companiesService.getLastPayrollTotals(this.clientCode, 0).subscribe((results: any[]) => {
      const now = new Date();
      const defaultQuarter = { year: now.getFullYear(), quarter: 1 };
      const date = new Date(Date.parse(results[0].PayDate));
      if (results.length > 0 && date.getFullYear() === now.getFullYear()) {
        defaultQuarter.quarter = GetQuarter(date);
      }
      this.form = this.fb.group({
        reportType: this.fb.control(ClientTotalsRecapTypes.Month, [Validators.required]),
        month: this.fb.control({ year: now.getFullYear(), value: now.getMonth() + 1 }),
        monthQuarterTotals: this.fb.control(false),
        quarter: this.fb.control(defaultQuarter)
      });
      this.form.get('reportType').valueChanges.subscribe(val => {
        if (val === ClientTotalsRecapTypes.Month) {
          this.form.get('month').addValidators(Validators.required);
          this.form.get('quarter').removeValidators(Validators.required);
        } else {
          this.form.get('quarter').addValidators(Validators.required);
          this.form.get('month').removeValidators(Validators.required);
        }
        this.form.get('month').updateValueAndValidity();
        this.form.get('quarter').updateValueAndValidity();
      });
    });
  }
  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const now = moment().format('MMDDYYYY');
    let filenameText;
    if (this.form.get('reportType').value === ClientTotalsRecapTypes.Month) {
      const month = args.StartDate.toLocaleString('default', { month: 'long' });
      filenameText = `${month} ${args.StartDate.getFullYear()}`;
    } else {
      const quarter = this.form.get('quarter').value;
      filenameText = `${quarter.year} ${quarter.quarter}`;
    }
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Client Totals Recap_${this.clientCode}_${filenameText}_${now}.${format}`,
      format
    });
  }
  private _getModel() {
    const type = this.form.get('reportType').value;
    let startDate;
    let endDate;
    if (type == ClientTotalsRecapTypes.Month) {
      const month: PickerValue = this.form.get('month').value;
      startDate = new Date(month.year, month.value - 1, 1);
      endDate = new Date(month.year, month.value, 0);
    } else {
      const quarter = this.form.get('quarter').value;
      startDate = GetQuarterStart(quarter.quarter, quarter.year);
      endDate = GetQuarterEnd(quarter.quarter, quarter.year);
    }

    const args: ClientTotalsRecapArgs = {
      StartDate: startDate,
      EndDate: endDate,
      DisplayMonthQuarterTotals: this.form.get('monthQuarterTotals').value
    };
    return args;
  }
}

export enum ClientTotalsRecapTypes {
  Month,
  Quarter
}
